import { useRef, useState, KeyboardEvent } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Home,
  Wind,
  Droplets,
  Leaf,
  PaintRoller,
  Hammer,
  ChevronLeft,
  ChevronRight,
  LucideIcon,
} from "lucide-react";
import BeforeAfterCard from "./BeforeAfterCard";
import BrowserCard from "./BrowserCard";
import SectionGlow from "./SectionGlow";
import { ease, duration } from "@/lib/motion";

type Industry = "roofing" | "hvac" | "plumbing" | "landscaping" | "painting" | "remodeling";

const INDUSTRIES: { id: Industry; label: string; icon: LucideIcon }[] = [
  { id: "roofing", label: "Roofing", icon: Home },
  { id: "hvac", label: "HVAC", icon: Wind },
  { id: "plumbing", label: "Plumbing", icon: Droplets },
  { id: "landscaping", label: "Landscaping", icon: Leaf },
  { id: "painting", label: "Painting", icon: PaintRoller },
  { id: "remodeling", label: "Remodeling", icon: Hammer },
];

type SliderCard = {
  beforeSrc: string;
  afterSrc: string;
  alt: string;
  label: string;
  caption: string;
};

type MockupCard = {
  src: string;
  alt: string;
  url: string;
  caption: string;
};

type IndustryShowcase = {
  hero: SliderCard;
  mockups: [MockupCard, MockupCard];
};

const SHOWCASES: Record<Industry, IndustryShowcase> = {
  roofing: {
    hero: {
      beforeSrc: "/showcases/roofing-1-before.jpg",
      afterSrc: "/showcases/roofing-1-after.jpg",
      alt: "Roofing contractor concept rebuild",
      label: "Roofing — Concept Rebuild",
      caption: "Built to capture storm-damage leads while your competitors are still checking voicemail.",
    },
    mockups: [
      {
        src: "/showcases/roofing-2.jpg",
        alt: "Roofing website — storm damage specialist",
        url: "stormroofingexperts.com",
        caption: "Storm-damage specialist with insurance-claim landing.",
      },
      {
        src: "/showcases/roofing-3.jpg",
        alt: "Roofing website — residential maintenance plans",
        url: "roofcareraleigh.com",
        caption: "Residential maintenance plans and inspection signup.",
      },
    ],
  },
  hvac: {
    hero: {
      beforeSrc: "/showcases/hvac-1-before.jpg",
      afterSrc: "/showcases/hvac-1-after.jpg",
      alt: "HVAC service concept rebuild",
      label: "HVAC — Concept Rebuild",
      caption: "Click-to-call hero and live tech availability — the homeowner who'd have bounced books in 30 seconds.",
    },
    mockups: [
      {
        src: "/showcases/hvac-2.jpg",
        alt: "HVAC website — installation and maintenance plans",
        url: "trustedhvacnc.com",
        caption: "Installation and maintenance plan comparison.",
      },
      {
        src: "/showcases/hvac-3.jpg",
        alt: "HVAC website — heat pump and ductless specialist",
        url: "ductlessheatpumpnc.com",
        caption: "Heat pump and ductless specialist with rebate calculator.",
      },
    ],
  },
  plumbing: {
    hero: {
      beforeSrc: "/showcases/plumbing-1-before.jpg",
      afterSrc: "/showcases/plumbing-1-after.jpg",
      alt: "Plumbing contractor concept rebuild",
      label: "Plumbing — Concept Rebuild",
      caption: "Emergency-first design that turns a 2am Google panic into a rolling truck.",
    },
    mockups: [
      {
        src: "/showcases/plumbing-2.jpg",
        alt: "Plumbing website — upfront flat-rate pricing",
        url: "trianglewaterworks.com",
        caption: "Residential site anchored on upfront flat-rate pricing.",
      },
      {
        src: "/showcases/plumbing-3.jpg",
        alt: "Plumbing website — commercial service contracts",
        url: "anchorfacilityplumbing.com",
        caption: "Commercial service contracts with portfolio dashboard.",
      },
    ],
  },
  landscaping: {
    hero: {
      beforeSrc: "/showcases/landscaping-1-before.jpg",
      afterSrc: "/showcases/landscaping-1-after.jpg",
      alt: "Landscaping studio concept rebuild",
      label: "Landscaping — Concept Rebuild",
      caption: "Editorial styling that attracts higher-budget clients who want a designer, not a mowing service.",
    },
    mockups: [
      {
        src: "/showcases/landscaping-2.jpg",
        alt: "Landscaping website — lawn care and maintenance",
        url: "greenlawnraleigh.com",
        caption: "Lawn care and maintenance plan signup.",
      },
      {
        src: "/showcases/landscaping-3.jpg",
        alt: "Landscaping website — hardscape and outdoor living",
        url: "hardscapestudio.com",
        caption: "Hardscape and outdoor living project showcase.",
      },
    ],
  },
  painting: {
    hero: {
      beforeSrc: "/showcases/painting-1-before.jpg",
      afterSrc: "/showcases/painting-1-after.jpg",
      alt: "Residential painting studio concept rebuild",
      label: "Painting — Concept Rebuild",
      caption: "Premium positioning that earns $8k color jobs instead of competing on $2k repaints.",
    },
    mockups: [
      {
        src: "/showcases/painting-2.jpg",
        alt: "Painting website — interior specialist lookbook",
        url: "studionorthinteriors.com",
        caption: "Interior specialist with a project lookbook above the fold.",
      },
      {
        src: "/showcases/painting-3.jpg",
        alt: "Painting website — exterior and pressure washing",
        url: "curbwiseexterior.com",
        caption: "Exterior specialist with before/after built into the hero.",
      },
    ],
  },
  remodeling: {
    hero: {
      beforeSrc: "/showcases/remodeling-1-before.jpg",
      afterSrc: "/showcases/remodeling-1-after.jpg",
      alt: "Kitchen and bath studio concept rebuild",
      label: "Remodeling — Concept Rebuild",
      caption: "Studio-grade pages that pre-qualify kitchen and bath leads — fewer tire-kickers, bigger projects.",
    },
    mockups: [
      {
        src: "/showcases/remodeling-2.jpg",
        alt: "Remodeling website — full home renovation",
        url: "wholehomeremodelnc.com",
        caption: "Full home renovation with project timeline.",
      },
      {
        src: "/showcases/remodeling-3.jpg",
        alt: "Remodeling website — basement and addition specialist",
        url: "basementadditionsnc.com",
        caption: "Basement and addition specialist with permit-ready process.",
      },
    ],
  },
};

const ShowcaseSection = () => {
  const [active, setActive] = useState<Industry>("roofing");
  const sectionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const selectIndustry = (id: Industry) => {
    setActive(id);
    const idx = INDUSTRIES.findIndex((i) => i.id === id);
    tabRefs.current[idx]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  };

  const currentIdx = INDUSTRIES.findIndex((i) => i.id === active);
  const goPrev = () =>
    selectIndustry(INDUSTRIES[(currentIdx - 1 + INDUSTRIES.length) % INDUSTRIES.length].id);
  const goNext = () =>
    selectIndustry(INDUSTRIES[(currentIdx + 1) % INDUSTRIES.length].id);

  // WAI-ARIA tablist keyboard pattern: arrow keys move focus AND activate.
  const onTabKeyDown = (e: KeyboardEvent<HTMLButtonElement>, idx: number) => {
    let nextIdx: number | null = null;
    if (e.key === "ArrowRight") nextIdx = (idx + 1) % INDUSTRIES.length;
    else if (e.key === "ArrowLeft") nextIdx = (idx - 1 + INDUSTRIES.length) % INDUSTRIES.length;
    else if (e.key === "Home") nextIdx = 0;
    else if (e.key === "End") nextIdx = INDUSTRIES.length - 1;
    if (nextIdx === null) return;
    e.preventDefault();
    selectIndustry(INDUSTRIES[nextIdx].id);
    tabRefs.current[nextIdx]?.focus();
  };

  const showcase = SHOWCASES[active];

  return (
    <section
      ref={sectionRef}
      id="showcase"
      className="relative bg-charcoal overflow-hidden"
      style={{ zIndex: 5 }}
      data-dark-section
    >
      <SectionGlow placement="top-center" />

      <div className="container mx-auto relative z-10 py-16 md:py-20 lg:py-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.1 }}
          transition={{ duration: duration.smooth, ease: ease.smooth }}
          className="text-center max-w-2xl mx-auto mb-8 md:mb-12"
        >
          <p className="text-xs font-semibold uppercase tracking-[0.25em] text-sage mb-3 md:text-sm">Examples</p>
          <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl text-cream mb-4 font-bold leading-[1.1]">
            Designed for <span className="text-cream">Your Industry.</span>
          </h2>
          <p className="text-sm text-cream/70 leading-snug md:text-base">
            Six industries. One real rebuild and two angle concepts each — pick your trade and see what we'd build.
          </p>
        </motion.div>

        <div className="flex flex-col items-center mb-8 md:mb-10">
          <div
            role="tablist"
            aria-label="Industry filter"
            className="flex gap-2 overflow-x-auto max-w-full px-4 pb-2 md:pb-0 md:overflow-visible md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {INDUSTRIES.map(({ id, label, icon: Icon }, idx) => {
              const isActive = active === id;
              return (
                <button
                  key={id}
                  ref={(el) => (tabRefs.current[idx] = el)}
                  role="tab"
                  id={`showcase-tab-${id}`}
                  aria-selected={isActive}
                  aria-controls="showcase-tabpanel"
                  tabIndex={isActive ? 0 : -1}
                  onClick={() => selectIndustry(id)}
                  onKeyDown={(e) => onTabKeyDown(e, idx)}
                  className={`relative shrink-0 inline-flex items-center gap-2 rounded-full border px-4 py-3.5 text-sm min-h-[44px] transition-[background-color,border-color,color,box-shadow,transform] duration-150 cursor-pointer md:px-5 ${
                    isActive
                      ? "bg-charcoal/60 border-sage-bright text-cream font-bold shadow-[inset_0_1px_0_hsl(var(--cream)/0.08),0_0_12px_hsl(var(--sage-bright)/0.18)]"
                      : "bg-transparent border-cream/15 text-cream/65 font-semibold hover:bg-cream/5 hover:border-cream/30 hover:text-cream hover:-translate-y-px active:translate-y-0"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5 md:h-4 md:w-4" />
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="group relative max-w-3xl mx-auto">
          <button
            type="button"
            aria-label="Previous industry"
            onClick={goPrev}
            className="hidden md:flex absolute left-2 xl:left-[-56px] top-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-charcoal/70 backdrop-blur-sm border border-cream/15 text-cream/80 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-[opacity,border-color,color,transform] duration-200 hover:border-sage-bright hover:text-cream hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60 focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Next industry"
            onClick={goNext}
            className="hidden md:flex absolute right-2 xl:right-[-56px] top-1/2 -translate-y-1/2 z-20 h-11 w-11 items-center justify-center rounded-full bg-charcoal/70 backdrop-blur-sm border border-cream/15 text-cream/80 opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-[opacity,border-color,color,transform] duration-200 hover:border-sage-bright hover:text-cream hover:scale-105 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage/60 focus-visible:ring-offset-2 focus-visible:ring-offset-charcoal"
          >
            <ChevronRight className="h-5 w-5" />
          </button>

          <AnimatePresence>
            <motion.div
              key={active}
              role="tabpanel"
              id="showcase-tabpanel"
              aria-labelledby={`showcase-tab-${active}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.18, ease: ease.smooth }}
            >
              <BeforeAfterCard {...showcase.hero} />
              <div className="mt-6 grid gap-6 md:mt-8 md:grid-cols-2">
                {showcase.mockups.map((m) => (
                  <BrowserCard key={m.src} {...m} />
                ))}
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default ShowcaseSection;
